import { useState, useEffect } from 'react';
import { isSuggestionsV2Enabled } from '@/lib/env';
import { 
  fetchSuggestionsV2Summary, 
  fetchSuggestionsV2,
  type SuggestionsV2Summary,
  type SuggestionsV2
} from '@/lib/suggestionsV2';
import { applySessionStatusesToSuggestions } from '@/lib/suggestionsStatusSession';
import { getPortalSuggestionsSummary } from '@/lib/suggestionsV2Client';
import { getSuggestionsWithSource, type SuggestionsResult } from '@/lib/suggestions';

const legacySuggestionsInflight = new Map<string, Promise<SuggestionsResult>>();

function buildLegacySuggestionsRequestKey(
  structureId: string,
  legacyData?: unknown[]
): string {
  let legacyKey = 'none';

  if (Array.isArray(legacyData) && legacyData.length > 0) {
    try {
      legacyKey = JSON.stringify(legacyData);
    } catch {
      legacyKey = `array:${legacyData.length}`;
    }
  }

  return `${structureId}::${legacyKey}`;
}

/**
 * Hook for fetching structure suggestions summary (Monitor tab)
 * - V2 mode: reads from Firestore `suggestions` collection
 * - Legacy mode: reads from structure document in Firestore
 * 
 * NOTE: No API endpoints are used. All data comes from Firestore directly.
 */
export function useStructureSuggestionsSummary(structureId: string | null | undefined) {
  const [data, setData] = useState<SuggestionsV2Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!structureId || !structureId.trim()) {
        setData(null);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await fetchSuggestionsV2Summary(structureId);
        if (mounted) {
          setData(result);
        }
      } catch (err: any) {
        console.error('[useStructureSuggestionsSummary] Error:', err);
        if (mounted) {
          setError(err?.message || 'Impossibile caricare il riepilogo suggestions');
          setData(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => { mounted = false; };
  }, [structureId]);

  return { data, loading, error };
}

/**
 * Hook for fetching structure suggestions with items (Improve tab)
 * - V2 mode: reads from Firestore `suggestions` collection
 * - Legacy mode: reads from structure document in Firestore
 * 
 * NOTE: No API endpoints are used. All data comes from Firestore directly.
 */
export function useStructureSuggestionsItems(structureId: string | null | undefined) {
  const [data, setData] = useState<SuggestionsV2 | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!structureId || !structureId.trim()) {
        setData(null);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await fetchSuggestionsV2(structureId);
        if (mounted) {
          setData(applySessionStatusesToSuggestions(result));
        }
      } catch (err: any) {
        console.error('[useStructureSuggestionsItems] Error:', err);
        if (mounted) {
          setError(err?.message || 'Impossibile caricare le suggestions');
          setData(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => { mounted = false; };
  }, [structureId]);

  return { data, loading, error };
}

/**
 * Portal suggestions summary types for V2
 */
type StatusCounts = {
  todo: number;
  in_progress: number;
  verifica_in_corso: number;
  done: number;
  rejected: number;
};

type StructureSummaryV2 = {
  structureId: string;
  structureName?: string;
  completionRate: number;
  counts: StatusCounts;
  source: 'collection' | 'legacy' | 'none';
};

export type PortalSummaryResponse = {
  totalStructures: number;
  structuresWithSuggestions: number;
  totalItems: number;
  byStatus: StatusCounts;
  structures: StructureSummaryV2[];
  nextCursor?: string;
};

type PortalSummaryOptions = {
  limit?: number;
  cursor?: string;
  includeEmpty?: boolean;
};

/**
 * Hook for fetching portal suggestions summary (Portal Monitor tab)
 * - V2 mode: attempts to use /nextapi/service/portals/{name}/suggestions/summary
 * - Legacy mode: returns empty data (portal-level summary not available in legacy)
 * 
 * WARNING: The portal endpoint may not exist in the current backoffice setup.
 * This hook will handle failures gracefully.
 * 
 * @deprecated Consider using structure-based queries instead of portal-level aggregation
 */
export function usePortalSuggestionsSummary(
  portalName: string | null | undefined,
  options?: PortalSummaryOptions
) {
  const [data, setData] = useState<PortalSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!portalName || !portalName.trim()) {
        setData(null);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const isV2 = isSuggestionsV2Enabled();
        
        if (!isV2) {
          // Legacy mode: portal summary not available
          if (mounted) {
            setData({
              totalStructures: 0,
              structuresWithSuggestions: 0,
              totalItems: 0,
              byStatus: { todo: 0, in_progress: 0, verifica_in_corso: 0, done: 0, rejected: 0 },
              structures: [],
            });
            setLoading(false);
          }
          return;
        }

        const response = await getPortalSuggestionsSummary(portalName, options);
        if (!response || typeof response !== 'object') {
          throw new Error('Invalid response format');
        }

        const summaryData = response as PortalSummaryResponse;
        if (mounted) {
          setData(summaryData);
        }
      } catch (err: any) {
        console.error('[usePortalSuggestionsSummary] Error:', err);
        if (mounted) {
          setError(err?.message || 'Impossibile caricare il riepilogo portale');
          setData(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => { mounted = false; };
  }, [portalName, options?.limit, options?.cursor, options?.includeEmpty]);

  return { data, loading, error };
}

/**
 * Legacy hook compatibility - uses repository layer for gradual migration
 * This is for components that still need the old SuggestionsResult format
 */
export function useLegacySuggestions(
  structureId: string | null | undefined,
  legacyData?: unknown[]
) {
  const [data, setData] = useState<SuggestionsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const requestKey = structureId && structureId.trim()
    ? buildLegacySuggestionsRequestKey(structureId.trim(), legacyData)
    : null;

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!structureId || !structureId.trim()) {
        setData(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const key = requestKey ?? buildLegacySuggestionsRequestKey(structureId, legacyData);
        const inflight = legacySuggestionsInflight.get(key);
        const request = inflight ?? getSuggestionsWithSource(structureId, legacyData);

        if (!inflight) {
          legacySuggestionsInflight.set(key, request);
        }

        const result = await request;
        if (mounted) {
          setData(result);
        }
      } catch (err) {
        console.error('[useLegacySuggestions] Error:', err);
        if (mounted) {
          setData({ suggestions: [], source: 'empty' });
        }
      } finally {
        if (requestKey) {
          legacySuggestionsInflight.delete(requestKey);
        }
        if (mounted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => { mounted = false; };
  }, [structureId, requestKey, legacyData]);

  return { data, loading };
}
